/**
 * Locomotion detection for the sprite (ADR-036 item 3): an entity is walking when its rendered position
 * changed within the last hold window. The hold covers frames between position updates, since the local
 * player moves once per tick (50 ms) while frames come faster, so the animation does not flicker to idle
 * between ticks.
 */

export const MOTION_HOLD_MS = 120;

/**
 * Tracks one entity's rendered position over time.
 */
export class MotionDetector {
  private x = Number.NaN;
  private y = Number.NaN;
  private lastMoveMs = Number.NEGATIVE_INFINITY;

  /**
   * Forgets the last position, so the next one is a first observation: a character that snapped (a respawn at a
   * shrine) is not drawn walking for the hold window after its jump.
   */
  public reset(): void {
    this.x = Number.NaN;
    this.y = Number.NaN;
    this.lastMoveMs = Number.NEGATIVE_INFINITY;
  }

  /**
   * Moves the last position by the change of the render origin (ADR-147 decision 6): a crossing moves every point drawn
   * by the same whole number of pixels, which is neither a step nor a stop.
   *
   * @param dx Across, in the units `update` takes.
   * @param dy The same down.
   */
  public shift(dx: number, dy: number): void {
    this.x += dx;
    this.y += dy;
  }

  /**
   * Records this frame's position.
   *
   * @param x Rendered x.
   * @param y Rendered y.
   * @param nowMs Frame time in ms.
   * @returns Whether the entity moved within the hold window ending now.
   */
  public update(x: number, y: number, nowMs: number): boolean {
    const first = Number.isNaN(this.x);
    if (!first && (x !== this.x || y !== this.y)) {
      this.lastMoveMs = nowMs;
    }
    this.x = x;
    this.y = y;
    return nowMs - this.lastMoveMs <= MOTION_HOLD_MS;
  }
}
